(() => {
  if (customElements.get('hellsinki-feedback')) return;

  class HellsinkiFeedback extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.ready = false;
      this.index = 0;
      this.render = this.render.bind(this);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.render, { once: true, signal: this.abort.signal });
      } else {
        this.render();
      }
      document.addEventListener('shopify:section:load', this.render, { signal: this.abort.signal });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    label(template, values) {
      return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, String(value)), template || '');
    }

    element(tag, className, text) {
      const node = document.createElement(tag);
      node.className = className;
      if (text) node.textContent = text;
      return node;
    }

    render() {
      if (this.ready) return;
      const source = this.querySelector('[data-feedback-source]')?.content;
      if (!source) return;
      const seen = new Set();
      const reviews = [...source.querySelectorAll('[data-feedback-review]')].map((slide) => {
        const text = slide.querySelector('[data-feedback-quote]')?.textContent.trim();
        const author = slide.querySelector('cite')?.textContent.trim();
        const image = slide.querySelector('img');
        const rating = Math.max(1, Math.min(5, Number(slide.dataset.rating) || 5));
        return { text, author, image, rating, location: slide.querySelector('[data-feedback-location]')?.textContent.trim() };
      }).filter((review) => {
        const key = `${review.author}|${review.text}`;
        if (!review.author || !review.text || seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 3);
      if (!reviews.length) return;

      this.track = this.querySelector('[data-feedback-track]');
      this.dots = this.querySelector('[data-feedback-dots]');
      this.status = this.querySelector('[data-feedback-status]');
      reviews.forEach((review, index) => {
        const slide = this.element('article', 'hellsinki-feedback__slide');
        slide.id = `${this.id}-slide-${index + 1}`;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', this.label(this.dataset.positionLabel, { current: index + 1, total: reviews.length }));
        const avatar = this.element('div', 'hellsinki-feedback__avatar');
        if (review.image) {
          const image = review.image.cloneNode();
          image.removeAttribute('id');
          image.className = '';
          image.width = 56;
          image.height = 56;
          image.sizes = '56px';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.alt = review.image.alt || '';
          avatar.append(image);
        } else {
          const words = review.author.split(/\s+/);
          avatar.textContent = `${words[0][0]}${words.length > 1 ? words.at(-1)[0] : ''}`.toUpperCase();
          avatar.setAttribute('aria-hidden', 'true');
        }
        const body = this.element('div', 'hellsinki-feedback__content');
        const quote = this.element('blockquote', 'hellsinki-feedback__quote', review.text);
        const more = this.element('button', 'hellsinki-feedback__more', this.dataset.moreLabel);
        quote.id = `${slide.id}-quote`;
        more.type = 'button';
        more.hidden = true;
        more.setAttribute('aria-expanded', 'false');
        more.setAttribute('aria-controls', quote.id);
        more.addEventListener('click', () => {
          const expanded = body.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', String(expanded));
          more.textContent = expanded ? this.dataset.lessLabel : this.dataset.moreLabel;
        }, { signal: this.abort.signal });
        const meta = this.element('div', 'hellsinki-feedback__meta');
        meta.append(this.element('cite', 'hellsinki-feedback__author', review.author));
        if (review.rating) {
          const rating = this.element('span', 'hellsinki-feedback__rating');
          rating.setAttribute('role', 'img');
          rating.setAttribute('aria-label', this.label(this.dataset.ratingLabel, { rating: review.rating }));
          for (let star = 0; star < 5; star += 1) {
            const icon = this.querySelector('[data-feedback-star]').content.firstElementChild.cloneNode(true);
            icon.classList.toggle('is-filled', star < review.rating);
            rating.append(icon);
          }
          meta.append(rating);
        }
        body.append(quote, more, meta);
        if (review.location) body.append(this.element('p', 'hellsinki-feedback__location', review.location));
        slide.append(avatar, body);
        this.track.append(slide);
        const dot = this.element('button', 'hellsinki-feedback__dot');
        dot.type = 'button';
        dot.setAttribute('aria-controls', slide.id);
        dot.setAttribute('aria-label', this.label(this.dataset.goLabel, { number: index + 1 }));
        dot.addEventListener('click', () => this.go(index), { signal: this.abort.signal });
        this.dots.append(dot);
      });

      this.ready = true;
      this.hidden = false;
      this.querySelector('[data-feedback-prev]').addEventListener('click', () => this.go(this.index - 1), { signal: this.abort.signal });
      this.querySelector('[data-feedback-next]').addEventListener('click', () => this.go(this.index + 1), { signal: this.abort.signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal: this.abort.signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update(Math.round(this.track.scrollLeft / this.track.clientWidth)));
      }, { passive: true, signal: this.abort.signal });
      this.resize = new ResizeObserver(() => {
        this.go(this.index, true);
        this.track.querySelectorAll('.hellsinki-feedback__content').forEach((body) => {
          if (body.classList.contains('is-expanded')) return;
          const quote = body.querySelector('blockquote');
          body.querySelector('button').hidden = quote.scrollHeight <= quote.clientHeight + 1;
        });
      });
      this.resize.observe(this.track);
      this.update(0);
    }

    update(index) {
      this.index = Math.max(0, Math.min(index, this.track.children.length - 1));
      [...this.dots.children].forEach((dot, number) => dot.setAttribute('aria-current', String(number === this.index)));
      [...this.track.children].forEach((slide, number) => {
        slide.inert = number !== this.index;
        slide.setAttribute('aria-hidden', String(number !== this.index));
      });
      this.status.textContent = this.label(this.dataset.positionLabel, { current: this.index + 1, total: this.track.children.length });
    }

    go(index, instant = false) {
      const total = this.track.children.length;
      const next = (index + total) % total;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: instant || reduced ? 'instant' : 'smooth' });
      if (instant) this.update(next);
    }
  }

  customElements.define('hellsinki-feedback', HellsinkiFeedback);
})();
